// frontend/src/App.tsx

import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import AppLayout from './components/AppLayout';
import Home from './pages/Home';
import Dashboard from "./pages/Dashboard";
import Tales from './pages/Tales';
import TaleDetail from './pages/TaleDetail';
import VideoProjects from './pages/VideoProjects';
import VideoProjectDetail from './pages/VideoProjectDetail';
import Storyboard from './pages/Storyboard';
import Editor from './pages/Editor';
import VoiceStudio from './pages/VoiceStudio';
import ThumbnailWorkspacePage from './pages/ThumbnailWorkspace';
import ImagesPage from './pages/Images';
import VideosPage from './pages/Videos';
import MyVideos from './pages/MyVideos';
import SettingsPage from './pages/Settings';
import CommandPalette from './components/CommandPalette';
import { Toaster } from "@/components/ui/toaster";
import { MarkerReviewScreen } from './components/markers/MarkerReviewScreen';
import { getMe } from './api';

export default function App() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check authentication status on mount
  useEffect(() => {
    getMe()
      .then(response => {
        // Check if the response contains user data (means authenticated)
        if (response.user) {
          setUser(response.user);
        } else {
          // Not authenticated, redirect to login
          window.location.href = '/login';
        }
      })
      .catch(error => {
        console.error('Auth check failed:', error);
        // On error, redirect to login
        window.location.href = '/login';
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
            <svg className="w-8 h-8 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Loading your workspace...</h2>
          <p className="text-slate-600 dark:text-slate-400">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, don't render anything (will redirect)
  if (!user) {
    return null;
  }

  return (
    <AppLayout>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<VideoProjects />} />
        <Route path="/dashboard" element={<VideoProjects />} />
        <Route path="/home" element={<Home />} />
        <Route path="/projects" element={<VideoProjects />} />
        <Route path="/projects/:id" element={<VideoProjectDetail />} />
        <Route path="/project/:id" element={<VideoProjectDetail />} />
        <Route path="/projects/:id/storyboard" element={<Storyboard />} />
        <Route path="/projects/:id/editor" element={<Editor />} />
        <Route path="/voice-studio/:id" element={<VoiceStudio />} />
        <Route path="/tales" element={<Tales />} />
        <Route path="/tales/:id/thumbnails/workspace" element={<ThumbnailWorkspacePage />} />
        <Route path="/tales/:id" element={<TaleDetail />} />
        <Route path="/project/:id/review" element={<MarkerReviewScreen />} />
        <Route path="/images" element={<ImagesPage />} />
        <Route path="/videos" element={<VideosPage />} />
        <Route path="/my-videos" element={<MyVideos />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global Command Palette */}
      <CommandPalette />

      {/* Toast notifications */}
      <Toaster />
    </AppLayout>
  );
}
